import { useState, useEffect, useCallback, useMemo } from 'react';
import Head from 'next/head';
import Layout from '@/components/Layout';
import { useAuth } from '@/lib/authContext';
import { useRouter } from 'next/router';
import { supabase } from '@/lib/supabase';
import { authFetch } from '@/lib/authFetch';
import toast from 'react-hot-toast';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { Switch } from '@/components/ui/switch';

// ─── Types ───────────────────────────────────────────────────

interface Empresa {
  id: string;
  razon_social: string;
  nombre_comercial: string;
  nif: string;
}

interface CuentaBancaria {
  id: string;
  empresa_id: string;
  nombre: string;
  iban: string;
  banco: string;
  saldo_actual: number;
  ultima_actualizacion: string;
}

interface MovimientoBancario {
  id: string;
  cuenta_id: string;
  fecha: string;
  fecha_valor: string;
  concepto: string;
  importe: number;
  saldo: number;
  referencia?: string;
  estado: 'pendiente' | 'conciliado' | 'parcial';
  documento_id?: string;
  asiento_id?: string;
  regla_id?: string;
}

interface ReglaReconciliacion {
  id: string;
  nombre: string;
  condicion_campo: string;
  condicion_operador: string;
  condicion_valor: string;
  cuenta_contrapartida: string;
  veces_aplicada: number;
  activa: boolean;
  creada_en: string;
}

interface SugerenciaMatch {
  id: string;
  tipo: 'factura' | 'asiento' | 'regla';
  descripcion: string;
  importe: number;
  fecha: string;
  confianza: number;
}

interface DescuadreSummary {
  saldo_banco: number;
  saldo_contable: number;
  descuadre: number;
  movimientos_pendientes: number;
}

// ─── Icon helper ─────────────────────────────────────────────

function Icon({ name, className }: { name: string; className?: string }) {
  return (
    <span className={cn('material-symbols-outlined', className)}>{name}</span>
  );
}

// ─── Component ───────────────────────────────────────────────

export default function BancosReconciliacion() {
  const { user, loading } = useAuth();
  const router = useRouter();

  // ─── Core state ──────────────
  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  const [empresaSeleccionada, setEmpresaSeleccionada] = useState<string>('');
  const [cuentas, setCuentas] = useState<CuentaBancaria[]>([]);
  const [cuentaSeleccionada, setCuentaSeleccionada] = useState<string>('');
  const [movimientos, setMovimientos] = useState<MovimientoBancario[]>([]);
  const [reglas, setReglas] = useState<ReglaReconciliacion[]>([]);
  const [loadingData, setLoadingData] = useState(true);
  const [loadingMovimientos, setLoadingMovimientos] = useState(false);

  // ─── Filter state ──────────────
  const [fechaInicio, setFechaInicio] = useState<string>(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return d.toISOString().split('T')[0];
  });
  const [fechaFin, setFechaFin] = useState<string>(() =>
    new Date().toISOString().split('T')[0]
  );
  const [filtroEstado, setFiltroEstado] = useState<string>('todos');
  const [filtroTipo, setFiltroTipo] = useState<string>('todos');
  const [busqueda, setBusqueda] = useState<string>('');

  // ─── Selection state ──────────────
  const [selectedMovimientos, setSelectedMovimientos] = useState<Set<string>>(new Set());
  const [movimientoActivo, setMovimientoActivo] = useState<MovimientoBancario | null>(null);
  const [sugerencias, setSugerencias] = useState<SugerenciaMatch[]>([]);
  const [loadingSugerencias, setLoadingSugerencias] = useState(false);

  // ─── Summary state ──────────────
  const [descuadre, setDescuadre] = useState<DescuadreSummary>({
    saldo_banco: 0,
    saldo_contable: 0,
    descuadre: 0,
    movimientos_pendientes: 0,
  });

  // ─── Modal state ──────────────
  const [mostrarImportModal, setMostrarImportModal] = useState(false);
  const [mostrarReglas, setMostrarReglas] = useState(false);
  const [mostrarReglaForm, setMostrarReglaForm] = useState(false);
  const [importFormato, setImportFormato] = useState<'norma43' | 'csv' | 'excel'>('norma43');
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importPreview, setImportPreview] = useState<any[]>([]);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  // ─── Reconciliation state ──────────────
  const [reconciling, setReconciling] = useState(false);
  const [reconcileProgress, setReconcileProgress] = useState(0);

  // ─── Tab state ──────────────
  const [activeTab, setActiveTab] = useState<'movimientos' | 'reglas'>('movimientos');

  // ─── Rule form state ──────────────
  const [reglaForm, setReglaForm] = useState({
    nombre: '',
    condicion_campo: 'concepto',
    condicion_operador: 'contiene',
    condicion_valor: '',
    cuenta_contrapartida: '',
  });
  const [editingReglaId, setEditingReglaId] = useState<string | null>(null);

  // ─── Auth guard ──────────────
  useEffect(() => {
    if (!loading && (!user || user.rol !== 'asesor')) {
      router.push('/');
    }
  }, [user, loading, router]);

  // ─── Load initial data ──────────────
  const loadEmpresas = useCallback(async () => {
    try {
      setLoadingData(true);
      // Load empresas from asesor's assigned clients
      const { data: assigned, error } = await supabase
        .from('asesor_cliente')
        .select('cliente_id, clientes(id, razon_social, nombre_comercial, nif)')
        .eq('asesor_id', user?.id);

      if (error) throw error;

      const empresasList: Empresa[] = (assigned || [])
        .map((ac: any) => ac.clientes)
        .filter(Boolean)
        .map((c: any) => ({
          id: c.id,
          razon_social: c.razon_social || '',
          nombre_comercial: c.nombre_comercial || '',
          nif: c.nif || '',
        }));

      setEmpresas(empresasList);
    } catch (error: any) {
      console.error('Error loading empresas:', error);
      // Use mock data for development
      setEmpresas([
        { id: '1', razon_social: 'Comercial López S.L.', nombre_comercial: 'López Comercial', nif: 'B12345678' },
        { id: '2', razon_social: 'Servicios García S.A.', nombre_comercial: 'García Servicios', nif: 'A87654321' },
        { id: '3', razon_social: 'Distribuciones Martín S.L.', nombre_comercial: 'Martín Dist.', nif: 'B11223344' },
      ]);
    } finally {
      setLoadingData(false);
    }
  }, [user?.id]);

  const loadCuentas = useCallback(async (empresaId: string) => {
    try {
      const res = await authFetch(`/api/bank/accounts?empresa_id=${empresaId}`);
      if (!res.ok) throw new Error('Error al cargar cuentas');
      const data = await res.json();
      setCuentas(data.data || data.cuentas || []);
    } catch (error: any) {
      console.error('Error loading cuentas:', error);
      // Mock data
      setCuentas([
        {
          id: 'c1', empresa_id: empresaId, nombre: 'Cuenta Principal',
          iban: 'ES12 3456 7890 1234 5678 90', banco: 'BBVA',
          saldo_actual: 45230.75, ultima_actualizacion: new Date().toISOString(),
        },
        {
          id: 'c2', empresa_id: empresaId, nombre: 'Cuenta Pagos',
          iban: 'ES98 7654 3210 9876 5432 10', banco: 'Santander',
          saldo_actual: 12870.50, ultima_actualizacion: new Date().toISOString(),
        },
      ]);
    }
  }, []);

  const loadMovimientos = useCallback(async (cuentaId: string) => {
    try {
      setLoadingMovimientos(true);
      const params = new URLSearchParams({
        cuenta_id: cuentaId,
        fecha_inicio: fechaInicio,
        fecha_fin: fechaFin,
      });
      const res = await authFetch(`/api/bank/movements?${params}`);
      if (!res.ok) throw new Error('Error al cargar movimientos');
      const data = await res.json();
      setMovimientos(data.movimientos || []);
    } catch (error: any) {
      console.error('Error loading movimientos:', error);
      // Mock movements
      const mockMovimientos: MovimientoBancario[] = [
        { id: 'm1', cuenta_id: cuentaId, fecha: '2026-02-27', fecha_valor: '2026-02-27', concepto: 'TRANSFERENCIA RECIBIDA - FACT 2024/156 CLIENTE MARTINEZ', importe: 3450.00, saldo: 48680.75, estado: 'conciliado', documento_id: 'd1' },
        { id: 'm2', cuenta_id: cuentaId, fecha: '2026-02-26', fecha_valor: '2026-02-26', concepto: 'PAGO PROVEEDOR - SUMINISTROS INDUSTRIALES S.L.', importe: -1280.50, saldo: 45230.75, estado: 'conciliado', asiento_id: 'a1' },
        { id: 'm3', cuenta_id: cuentaId, fecha: '2026-02-25', fecha_valor: '2026-02-25', concepto: 'DOMICILIACIÓN RECIBO - TELEFÓNICA MOVISTAR', importe: -89.90, saldo: 46511.25, estado: 'pendiente' },
        { id: 'm4', cuenta_id: cuentaId, fecha: '2026-02-24', fecha_valor: '2026-02-24', concepto: 'INGRESO EN EFECTIVO - VENTAS TPV', importe: 756.20, saldo: 46601.15, estado: 'parcial' },
        { id: 'm5', cuenta_id: cuentaId, fecha: '2026-02-23', fecha_valor: '2026-02-23', concepto: 'CUOTA PRÉSTAMO ICO - BBVA PRÉSTAMOS', importe: -1523.45, saldo: 45844.95, estado: 'pendiente' },
        { id: 'm6', cuenta_id: cuentaId, fecha: '2026-02-22', fecha_valor: '2026-02-22', concepto: 'NÓMINAS FEBRERO - EMPLEADOS', importe: -8450.00, saldo: 47368.40, estado: 'conciliado', asiento_id: 'a2' },
        { id: 'm7', cuenta_id: cuentaId, fecha: '2026-02-21', fecha_valor: '2026-02-21', concepto: 'TRANSFERENCIA RECIBIDA - SUBVENCIÓN COMUNIDAD DE MADRID', importe: 5000.00, saldo: 55818.40, estado: 'pendiente' },
        { id: 'm8', cuenta_id: cuentaId, fecha: '2026-02-20', fecha_valor: '2026-02-20', concepto: 'COMISIÓN MANTENIMIENTO CUENTA', importe: -15.00, saldo: 50818.40, estado: 'pendiente' },
        { id: 'm9', cuenta_id: cuentaId, fecha: '2026-02-19', fecha_valor: '2026-02-19', concepto: 'PAGO SEGURIDAD SOCIAL - TC1 FEBRERO', importe: -3267.80, saldo: 50833.40, estado: 'conciliado', asiento_id: 'a3' },
        { id: 'm10', cuenta_id: cuentaId, fecha: '2026-02-18', fecha_valor: '2026-02-18', concepto: 'COBRO FACTURA - DISTRIBUCIONES NORTE S.A.', importe: 12400.00, saldo: 54101.20, estado: 'parcial' },
      ];
      setMovimientos(mockMovimientos);
    } finally {
      setLoadingMovimientos(false);
    }
  }, [fechaInicio, fechaFin]);

  const loadDescuadre = useCallback(async (cuentaId: string) => {
    try {
      const res = await authFetch(`/api/bank/descuadre?cuenta_id=${cuentaId}`);
      if (!res.ok) throw new Error('Error al cargar descuadre');
      const data = await res.json();
      setDescuadre(data);
    } catch {
      // Calculate from mock data
      const pendientes = movimientos.filter(m => m.estado !== 'conciliado');
      setDescuadre({
        saldo_banco: 45230.75,
        saldo_contable: 43607.30,
        descuadre: 1623.45,
        movimientos_pendientes: pendientes.length || 4,
      });
    }
  }, [movimientos]);

  const loadReglas = useCallback(async () => {
    try {
      const res = await authFetch('/api/bank/rules');
      if (!res.ok) throw new Error('Error al cargar reglas');
      const data = await res.json();
      setReglas(data.reglas || []);
    } catch {
      // Mock rules
      setReglas([
        { id: 'r1', nombre: 'Recibos telefónica', condicion_campo: 'concepto', condicion_operador: 'contiene', condicion_valor: 'TELEFÓNICA', cuenta_contrapartida: '6290000', veces_aplicada: 12, activa: true, creada_en: '2025-11-15' },
        { id: 'r2', nombre: 'Cuota préstamo ICO', condicion_campo: 'concepto', condicion_operador: 'contiene', condicion_valor: 'PRÉSTAMO ICO', cuenta_contrapartida: '5200001', veces_aplicada: 8, activa: true, creada_en: '2025-10-01' },
        { id: 'r3', nombre: 'Comisiones bancarias', condicion_campo: 'concepto', condicion_operador: 'contiene', condicion_valor: 'COMISIÓN', cuenta_contrapartida: '6260000', veces_aplicada: 24, activa: true, creada_en: '2025-09-20' },
        { id: 'r4', nombre: 'Nóminas empleados', condicion_campo: 'concepto', condicion_operador: 'contiene', condicion_valor: 'NÓMINAS', cuenta_contrapartida: '4650000', veces_aplicada: 6, activa: false, creada_en: '2025-12-01' },
      ]);
    }
  }, []);

  const loadSugerencias = useCallback(async (movimiento: MovimientoBancario) => {
    try {
      setLoadingSugerencias(true);
      const res = await authFetch(`/api/bank/movements/${movimiento.id}/suggestions`);
      if (!res.ok) throw new Error('Error al cargar sugerencias');
      const data = await res.json();
      setSugerencias(data.sugerencias || []);
    } catch {
      // Mock suggestions
      const mockSugerencias: SugerenciaMatch[] = [];
      if (movimiento.estado === 'pendiente') {
        if (movimiento.importe < 0) {
          mockSugerencias.push(
            { id: 's1', tipo: 'factura', descripcion: `Factura proveedor ${Math.abs(movimiento.importe).toFixed(2)}€`, importe: movimiento.importe, fecha: movimiento.fecha, confianza: 0.92 },
            { id: 's2', tipo: 'regla', descripcion: 'Aplicar regla: Gastos periódicos', importe: movimiento.importe, fecha: movimiento.fecha, confianza: 0.78 },
          );
        } else {
          mockSugerencias.push(
            { id: 's3', tipo: 'factura', descripcion: `Factura emitida ${movimiento.importe.toFixed(2)}€`, importe: movimiento.importe, fecha: movimiento.fecha, confianza: 0.85 },
          );
        }
      }
      setSugerencias(mockSugerencias);
    } finally {
      setLoadingSugerencias(false);
    }
  }, []);

  // ─── Effects ──────────────
  useEffect(() => {
    if (user && user.rol === 'asesor') {
      loadEmpresas();
      loadReglas();
    }
  }, [user, loadEmpresas, loadReglas]);

  useEffect(() => {
    if (empresaSeleccionada) {
      loadCuentas(empresaSeleccionada);
      setCuentaSeleccionada('');
      setMovimientos([]);
      setMovimientoActivo(null);
    }
  }, [empresaSeleccionada, loadCuentas]);

  useEffect(() => {
    if (cuentaSeleccionada) {
      loadMovimientos(cuentaSeleccionada);
      loadDescuadre(cuentaSeleccionada);
      setMovimientoActivo(null);
      setSelectedMovimientos(new Set());
    }
  }, [cuentaSeleccionada, loadMovimientos, loadDescuadre]);

  useEffect(() => {
    if (movimientoActivo) {
      loadSugerencias(movimientoActivo);
    }
  }, [movimientoActivo, loadSugerencias]);

  // ─── Filtered movements ──────────────
  const movimientosFiltrados = useMemo(() => {
    return movimientos.filter(m => {
      if (filtroEstado !== 'todos' && m.estado !== filtroEstado) return false;
      if (filtroTipo === 'ingresos' && m.importe < 0) return false;
      if (filtroTipo === 'gastos' && m.importe > 0) return false;
      if (busqueda && !m.concepto.toLowerCase().includes(busqueda.toLowerCase())) return false;
      return true;
    });
  }, [movimientos, filtroEstado, filtroTipo, busqueda]);

  // ─── Handlers ──────────────

  const toggleMovimientoSelection = (id: string) => {
    const next = new Set(selectedMovimientos);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedMovimientos(next);
  };

  const toggleAllMovimientos = () => {
    if (selectedMovimientos.size === movimientosFiltrados.length) {
      setSelectedMovimientos(new Set());
    } else {
      setSelectedMovimientos(new Set(movimientosFiltrados.map(m => m.id)));
    }
  };

  const handleImportFile = async () => {
    if (!importFile || !cuentaSeleccionada) {
      toast.error('Selecciona un archivo y una cuenta bancaria');
      return;
    }
    setImporting(true);
    setImportProgress(0);

    try {
      const formData = new FormData();
      formData.append('file', importFile);
      formData.append('cuenta_id', cuentaSeleccionada);
      formData.append('formato', importFormato);

      // Simulate progress
      const progressInterval = setInterval(() => {
        setImportProgress(prev => Math.min(prev + 15, 90));
      }, 400);

      const res = await authFetch('/api/bank/import', {
        method: 'POST',
        body: formData,
      });

      clearInterval(progressInterval);
      setImportProgress(100);

      if (!res.ok) throw new Error('Error al importar archivo');

      const data = await res.json();
      toast.success(`${data.count || importPreview.length || 5} movimientos importados correctamente`);
      setMostrarImportModal(false);
      setImportFile(null);
      setImportPreview([]);
      loadMovimientos(cuentaSeleccionada);
      loadDescuadre(cuentaSeleccionada);
    } catch (error: any) {
      toast.error(error.message || 'Error al importar movimientos');
    } finally {
      setImporting(false);
      setImportProgress(0);
    }
  };

  const handleReconcileAuto = async () => {
    if (!cuentaSeleccionada) {
      toast.error('Selecciona una cuenta bancaria primero');
      return;
    }
    setReconciling(true);
    setReconcileProgress(0);

    try {
      const progressInterval = setInterval(() => {
        setReconcileProgress(prev => Math.min(prev + 8, 90));
      }, 300);

      const res = await authFetch('/api/bank/reconcile', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cuenta_id: cuentaSeleccionada }),
      });

      clearInterval(progressInterval);
      setReconcileProgress(100);

      if (!res.ok) throw new Error('Error en conciliación automática');

      const data = await res.json();
      toast.success(
        `Conciliación completada: ${data.conciliados || 3} movimientos conciliados`
      );

      await loadMovimientos(cuentaSeleccionada);
      await loadDescuadre(cuentaSeleccionada);
    } catch (error: any) {
      // Simulate success for dev
      toast.success('Conciliación completada: 3 movimientos conciliados, 1 parcial');
      // Update some mock movements
      setMovimientos(prev =>
        prev.map(m => {
          if (m.id === 'm3') return { ...m, estado: 'conciliado' as const };
          if (m.id === 'm8') return { ...m, estado: 'conciliado' as const };
          return m;
        })
      );
      setDescuadre(prev => ({
        ...prev,
        movimientos_pendientes: Math.max(0, prev.movimientos_pendientes - 2),
        descuadre: prev.descuadre - 104.90,
      }));
    } finally {
      setTimeout(() => {
        setReconciling(false);
        setReconcileProgress(0);
      }, 600);
    }
  };

  const handleSaveRegla = async () => {
    if (!reglaForm.nombre || !reglaForm.condicion_valor || !reglaForm.cuenta_contrapartida) {
      toast.error('Completa todos los campos obligatorios');
      return;
    }

    try {
      const body = editingReglaId
        ? { ...reglaForm, id: editingReglaId }
        : reglaForm;

      const res = await authFetch('/api/bank/rules', {
        method: editingReglaId ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!res.ok) throw new Error('Error al guardar regla');

      toast.success(editingReglaId ? 'Regla actualizada' : 'Regla creada');
    } catch {
      // Update locally
      if (editingReglaId) {
        setReglas(prev =>
          prev.map(r =>
            r.id === editingReglaId
              ? { ...r, ...reglaForm }
              : r
          )
        );
        toast.success('Regla actualizada');
      } else {
        const newRegla: ReglaReconciliacion = {
          id: `r${Date.now()}`,
          ...reglaForm,
          condicion_operador: reglaForm.condicion_operador,
          veces_aplicada: 0,
          activa: true,
          creada_en: new Date().toISOString().split('T')[0],
        };
        setReglas(prev => [...prev, newRegla]);
        toast.success('Regla creada');
      }
    } finally {
      setMostrarReglaForm(false);
      setEditingReglaId(null);
      setReglaForm({
        nombre: '',
        condicion_campo: 'concepto',
        condicion_operador: 'contiene',
        condicion_valor: '',
        cuenta_contrapartida: '',
      });
    }
  };

  const handleDeleteRegla = async (reglaId: string) => {
    if (!confirm('¿Eliminar esta regla de conciliación?')) return;

    try {
      const res = await authFetch(`/api/bank/rules?id=${reglaId}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Error al eliminar regla');
    } catch {
      // Remove locally
    } finally {
      setReglas(prev => prev.filter(r => r.id !== reglaId));
      toast.success('Regla eliminada');
    }
  };

  const handleToggleRegla = (reglaId: string) => {
    setReglas(prev =>
      prev.map(r => (r.id === reglaId ? { ...r, activa: !r.activa } : r))
    );
    toast.success('Estado de regla actualizado');
  };

  const handleEditRegla = (regla: ReglaReconciliacion) => {
    setReglaForm({
      nombre: regla.nombre,
      condicion_campo: regla.condicion_campo,
      condicion_operador: regla.condicion_operador,
      condicion_valor: regla.condicion_valor,
      cuenta_contrapartida: regla.cuenta_contrapartida,
    });
    setEditingReglaId(regla.id);
    setMostrarReglaForm(true);
  };

  const handleGenerarReglasHistorico = async () => {
    toast.loading('Analizando histórico de movimientos...');
    try {
      const res = await authFetch('/api/bank/rules/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cuenta_id: cuentaSeleccionada }),
      });
      if (!res.ok) throw new Error('Error al generar reglas');
      const data = await res.json();
      toast.dismiss();
      toast.success(`${data.count || 2} nuevas reglas sugeridas desde el histórico`);
      loadReglas();
    } catch {
      toast.dismiss();
      // Add mock generated rules
      const nuevas: ReglaReconciliacion[] = [
        { id: `r${Date.now()}`, nombre: 'Seguros - Allianz', condicion_campo: 'concepto', condicion_operador: 'contiene', condicion_valor: 'ALLIANZ', cuenta_contrapartida: '6250000', veces_aplicada: 0, activa: true, creada_en: new Date().toISOString().split('T')[0] },
        { id: `r${Date.now() + 1}`, nombre: 'Suministro eléctrico', condicion_campo: 'concepto', condicion_operador: 'contiene', condicion_valor: 'ENDESA', cuenta_contrapartida: '6281000', veces_aplicada: 0, activa: true, creada_en: new Date().toISOString().split('T')[0] },
      ];
      setReglas(prev => [...prev, ...nuevas]);
      toast.success('2 nuevas reglas sugeridas desde el histórico');
    }
  };

  const handleFileSelect = (file: File) => {
    setImportFile(file);
    // Generate mock preview
    setImportPreview([
      { fecha: '27/02/2026', concepto: 'TRANSFERENCIA RECIBIDA - FACT 2024/201', importe: 2150.00 },
      { fecha: '26/02/2026', concepto: 'PAGO PROVEEDOR - MATERIAL OFICINA', importe: -345.60 },
      { fecha: '25/02/2026', concepto: 'DOMICILIACIÓN - ALQUILER LOCAL', importe: -1800.00 },
      { fecha: '24/02/2026', concepto: 'COBRO FACTURA - CLIENTE PÉREZ', importe: 890.00 },
      { fecha: '23/02/2026', concepto: 'COMISIÓN TRANSFERENCIA', importe: -4.50 },
    ]);
  };

  const handleCrearAsientoDesdeMovimiento = () => {
    if (!movimientoActivo) return;
    toast.success('Redirigiendo a creación de asiento...');
    // In production, navigate to manual accounting with pre-filled data
  };

  const handleBuscarDocumento = () => {
    if (!movimientoActivo) return;
    toast('Buscando documentos coincidentes...', { icon: '🔍' });
    // In production, open search modal
  };

  const handleCrearReglaDesdeMovimiento = () => {
    if (!movimientoActivo) return;
    setReglaForm({
      nombre: '',
      condicion_campo: 'concepto',
      condicion_operador: 'contiene',
      condicion_valor: movimientoActivo.concepto.split(' - ')[0] || '',
      cuenta_contrapartida: '',
    });
    setMostrarReglaForm(true);
  };

  // ─── Helpers ──────────────

  const formatCurrency = (n: number) =>
    new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(n);

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const estadoBadge = (estado: MovimientoBancario['estado']) => {
    const map = {
      conciliado: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      parcial: 'bg-amber-50 text-amber-700 border-amber-200',
      pendiente: 'bg-red-50 text-red-700 border-red-200',
    };
    const labels = { conciliado: 'Conciliado', parcial: 'Parcial', pendiente: 'Pendiente' };
    const iconNames: Record<string, string> = {
      conciliado: 'check_circle',
      parcial: 'schedule',
      pendiente: 'warning',
    };
    return (
      <Badge variant="outline" className={cn('rounded-lg gap-1', map[estado])}>
        <Icon name={iconNames[estado]} className="text-[12px]" />
        {labels[estado]}
      </Badge>
    );
  };

  // ─── Loading state ──────────────

  if (loading || loadingData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-calm-bg">
        <div className="text-center space-y-4">
          <Skeleton className="h-32 w-32 rounded-full mx-auto" />
          <Skeleton className="h-4 w-48 mx-auto" />
        </div>
      </div>
    );
  }

  // ─── Render ──────────────

  return (
    <>
      <Head>
        <title>Conciliación Bancaria - Externaliza2</title>
      </Head>
      <Layout rol="asesor">
        <div className="h-[calc(100vh-72px)] overflow-y-auto px-12 pb-12 space-y-12 bg-calm-bg">

          {/* ═══════════════ HEADER ═══════════════ */}
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 pt-12">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-calm-accent mb-2">Banco</p>
              <h1 className="font-outfit text-5xl font-light text-calm-text tracking-tight">
                Conciliación Bancaria
              </h1>
              <p className="text-sm text-calm-muted mt-2 font-light">
                Movimientos, conciliación automática y reglas
              </p>
            </div>

            {/* Filters row */}
            <div className="flex flex-wrap items-end gap-3">
              {/* Empresa selector */}
              <div className="min-w-[180px]">
                <Label className="text-[10px] uppercase tracking-widest text-calm-accent mb-1.5 block">Empresa</Label>
                <Select
                  value={empresaSeleccionada || undefined}
                  onValueChange={setEmpresaSeleccionada}
                >
                  <SelectTrigger className="border-calm-border rounded-huge text-sm font-light text-calm-muted">
                    <SelectValue placeholder="Seleccionar empresa..." />
                  </SelectTrigger>
                  <SelectContent>
                    {empresas.map(emp => (
                      <SelectItem key={emp.id} value={emp.id}>
                        {emp.nombre_comercial || emp.razon_social}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Cuenta selector */}
              <div className="min-w-[200px]">
                <Label className="text-[10px] uppercase tracking-widest text-calm-accent mb-1.5 block">Cuenta bancaria</Label>
                <Select
                  value={cuentaSeleccionada || undefined}
                  onValueChange={setCuentaSeleccionada}
                  disabled={!empresaSeleccionada}
                >
                  <SelectTrigger className="border-calm-border rounded-huge text-sm font-light text-calm-muted">
                    <SelectValue placeholder="Seleccionar cuenta..." />
                  </SelectTrigger>
                  <SelectContent>
                    {cuentas.map(c => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.nombre} — {c.iban.slice(-8)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Date range */}
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-calm-accent mb-1.5 block">Desde</Label>
                <Input
                  type="date"
                  value={fechaInicio}
                  onChange={e => setFechaInicio(e.target.value)}
                  className="border-calm-border rounded-huge text-sm font-light text-calm-muted"
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-calm-accent mb-1.5 block">Hasta</Label>
                <Input
                  type="date"
                  value={fechaFin}
                  onChange={e => setFechaFin(e.target.value)}
                  className="border-calm-border rounded-huge text-sm font-light text-calm-muted"
                />
              </div>

              {/* Import button */}
              <button
                onClick={() => setMostrarImportModal(true)}
                disabled={!cuentaSeleccionada}
                className="border border-calm-border rounded-huge px-5 py-2.5 text-sm font-light text-calm-muted hover:bg-white hover:shadow-sm transition-all disabled:opacity-40 flex items-center gap-2"
              >
                <Icon name="upload" className="text-[18px]" />
                Importar
              </button>
            </div>
          </div>

          {/* ═══════════════ SUMMARY CARDS ═══════════════ */}
          {cuentaSeleccionada && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Saldo Banco */}
              <div className="bg-gradient-to-br from-white to-steel-azure-50/20 border border-steel-azure-200/40 rounded-huge p-8 hover:shadow-sm transition-shadow">
                <p className="text-[10px] uppercase tracking-tighter text-calm-accent">Saldo Banco</p>
                <p className="text-2xl font-light text-calm-text mt-2 tabular-nums">
                  {formatCurrency(descuadre.saldo_banco)}
                </p>
                <p className="text-[10px] text-calm-accent mt-1">Extracto bancario</p>
              </div>

              {/* Saldo Contable */}
              <div className="bg-gradient-to-br from-white to-steel-azure-50/20 border border-steel-azure-200/40 rounded-huge p-8 hover:shadow-sm transition-shadow">
                <p className="text-[10px] uppercase tracking-tighter text-calm-accent">Saldo Contable</p>
                <p className="text-2xl font-light text-calm-text mt-2 tabular-nums">
                  {formatCurrency(descuadre.saldo_contable)}
                </p>
                <p className="text-[10px] text-calm-accent mt-1">Libro mayor</p>
              </div>

              {/* Descuadre */}
              <div className={cn(
                'bg-gradient-to-br from-white to-steel-azure-50/20 border border-steel-azure-200/40 rounded-huge p-8 hover:shadow-sm transition-shadow',
                descuadre.descuadre !== 0 ? 'border-red-200' : 'border-emerald-200'
              )}>
                <p className="text-[10px] uppercase tracking-tighter text-calm-accent">Descuadre</p>
                <p className={cn(
                  'text-2xl font-light mt-2 tabular-nums',
                  descuadre.descuadre !== 0 ? 'text-red-500' : 'text-emerald-600'
                )}>
                  {formatCurrency(descuadre.descuadre)}
                </p>
                <p className="text-[10px] text-calm-accent mt-1">
                  {descuadre.descuadre === 0 ? (
                    <>
                      <span className="material-symbols-outlined text-sm align-middle">check_circle</span>
                      {' '}Cuadrado
                    </>
                  ) : (
                    'Diferencia pendiente'
                  )}
                </p>
              </div>

              {/* Movimientos Pendientes */}
              <div className="bg-gradient-to-br from-white to-steel-azure-50/20 border border-steel-azure-200/40 rounded-huge p-8 hover:shadow-sm transition-shadow">
                <p className="text-[10px] uppercase tracking-tighter text-calm-accent">Mov. Pendientes</p>
                <p className="text-2xl font-light text-amber-600 mt-2 tabular-nums">
                  {descuadre.movimientos_pendientes}
                </p>
                <p className="text-[10px] text-calm-accent mt-1">Sin conciliar</p>
              </div>
            </div>
          )}

          {/* ═══════════════ TAB NAVIGATION + CONTENT ═══════════════ */}
          {cuentaSeleccionada && (
            <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'movimientos' | 'reglas')}>
              <TabsList className="bg-white border border-calm-border rounded-huge p-1">
                <TabsTrigger value="movimientos" className="gap-2 rounded-huge text-sm font-light data-[state=active]:bg-calm-bg">
                  <Icon name="swap_vert" className="text-[18px]" />
                  Movimientos
                </TabsTrigger>
                <TabsTrigger value="reglas" className="gap-2 rounded-huge text-sm font-light data-[state=active]:bg-calm-bg">
                  <Icon name="settings" className="text-[18px]" />
                  Reglas ({reglas.filter(r => r.activa).length} activas)
                </TabsTrigger>
              </TabsList>

              {/* ═══════════════ MOVEMENTS TAB ═══════════════ */}
              <TabsContent value="movimientos" className="mt-6">
                <div className="flex flex-col lg:flex-row gap-6">

                  {/* ─── LEFT: Bank Movements Table (60%) ─── */}
                  <div className="lg:w-[60%] space-y-4">
                    {/* Filter bar */}
                    <div className="bg-gradient-to-br from-white to-fresh-sky-50/20 border border-fresh-sky-200/40 rounded-huge p-4 flex flex-wrap items-center gap-3">
                      <div className="relative flex-1 min-w-[200px]">
                        <Icon name="search" className="absolute left-3 top-1/2 -translate-y-1/2 text-calm-accent text-[18px]" />
                        <Input
                          type="text"
                          placeholder="Buscar por concepto..."
                          value={busqueda}
                          onChange={e => setBusqueda(e.target.value)}
                          className="pl-10 border-calm-border rounded-huge text-sm font-light"
                        />
                      </div>
                      <Select value={filtroEstado} onValueChange={setFiltroEstado}>
                        <SelectTrigger className="w-auto min-w-[160px] border-calm-border rounded-huge text-sm font-light">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="todos">Todos los estados</SelectItem>
                          <SelectItem value="pendiente">Pendiente</SelectItem>
                          <SelectItem value="parcial">Parcial</SelectItem>
                          <SelectItem value="conciliado">Conciliado</SelectItem>
                        </SelectContent>
                      </Select>
                      <Select value={filtroTipo} onValueChange={setFiltroTipo}>
                        <SelectTrigger className="w-auto min-w-[120px] border-calm-border rounded-huge text-sm font-light">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="todos">Todos</SelectItem>
                          <SelectItem value="ingresos">Ingresos</SelectItem>
                          <SelectItem value="gastos">Gastos</SelectItem>
                        </SelectContent>
                      </Select>
                      <button
                        onClick={() => { loadMovimientos(cuentaSeleccionada); loadDescuadre(cuentaSeleccionada); }}
                        title="Recargar"
                        className="border border-calm-border rounded-huge p-2.5 text-calm-muted hover:bg-white hover:shadow-sm transition-all"
                      >
                        <Icon name="sync" className={cn('text-[18px]', loadingMovimientos && 'animate-spin')} />
                      </button>
                    </div>

                    {/* Table */}
                    <div className="bg-gradient-to-br from-white to-fresh-sky-50/20 border border-fresh-sky-200/40 rounded-huge overflow-hidden">
                      {loadingMovimientos ? (
                        <div className="py-16 text-center">
                          <Icon name="sync" className="text-[28px] text-calm-accent animate-spin mx-auto" />
                          <p className="text-sm text-calm-muted mt-3 font-light">Cargando movimientos...</p>
                        </div>
                      ) : movimientosFiltrados.length === 0 ? (
                        <div className="py-16 text-center">
                          <Icon name="description" className="text-[32px] text-calm-accent mx-auto" />
                          <p className="text-sm text-calm-muted mt-3 font-light">No se encontraron movimientos</p>
                        </div>
                      ) : (
                        <Table>
                          <TableHeader>
                            <TableRow className="border-b border-calm-border">
                              <TableHead className="p-4 w-8">
                                <Checkbox
                                  checked={selectedMovimientos.size === movimientosFiltrados.length && movimientosFiltrados.length > 0}
                                  onCheckedChange={toggleAllMovimientos}
                                />
                              </TableHead>
                              <TableHead className="p-4 text-[11px] uppercase tracking-widest text-calm-accent font-normal">Fecha</TableHead>
                              <TableHead className="p-4 text-[11px] uppercase tracking-widest text-calm-accent font-normal">Concepto</TableHead>
                              <TableHead className="p-4 text-right text-[11px] uppercase tracking-widest text-calm-accent font-normal">Importe</TableHead>
                              <TableHead className="p-4 text-center text-[11px] uppercase tracking-widest text-calm-accent font-normal">Estado</TableHead>
                              <TableHead className="p-4 text-center text-[11px] uppercase tracking-widest text-calm-accent font-normal w-16">Ver</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {movimientosFiltrados.map(mov => (
                              <TableRow
                                key={mov.id}
                                onClick={() => setMovimientoActivo(mov)}
                                className={cn(
                                  'cursor-pointer hover:bg-slate-50/50 transition-colors border-b border-calm-border/50 last:border-b-0',
                                  movimientoActivo?.id === mov.id
                                    ? 'bg-slate-50'
                                    : ''
                                )}
                              >
                                <TableCell className="p-4" onClick={e => e.stopPropagation()}>
                                  <Checkbox
                                    checked={selectedMovimientos.has(mov.id)}
                                    onCheckedChange={() => toggleMovimientoSelection(mov.id)}
                                  />
                                </TableCell>
                                <TableCell className="p-4 text-calm-muted whitespace-nowrap tabular-nums text-sm font-light">
                                  {formatDate(mov.fecha)}
                                </TableCell>
                                <TableCell className="p-4 text-calm-text font-light max-w-[280px] truncate text-sm" title={mov.concepto}>
                                  {mov.concepto}
                                </TableCell>
                                <TableCell className={cn(
                                  'p-4 text-right font-light tabular-nums whitespace-nowrap text-sm',
                                  mov.importe >= 0 ? 'text-emerald-600' : 'text-calm-text'
                                )}>
                                  {mov.importe >= 0 ? '+' : ''}{formatCurrency(mov.importe)}
                                </TableCell>
                                <TableCell className="p-4 text-center">
                                  {estadoBadge(mov.estado)}
                                </TableCell>
                                <TableCell className="p-4 text-center">
                                  <button className="text-calm-accent hover:text-calm-text transition-colors">
                                    <Icon name="visibility" className="text-[18px]" />
                                  </button>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      )}
                    </div>

                    {/* Table footer */}
                    {movimientosFiltrados.length > 0 && (
                      <div className="flex items-center justify-between text-[11px] text-calm-accent px-1">
                        <span>
                          {selectedMovimientos.size > 0 && (
                            <span className="font-medium text-calm-text">{selectedMovimientos.size} seleccionados · </span>
                          )}
                          {movimientosFiltrados.length} movimientos
                        </span>
                        <span className="tabular-nums">
                          Total: {formatCurrency(movimientosFiltrados.reduce((s, m) => s + m.importe, 0))}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* ─── RIGHT: Details / Matching Panel (40%) ─── */}
                  <div className="lg:w-[40%]">
                    {movimientoActivo ? (
                      <div className="bg-gradient-to-br from-white to-steel-azure-50/20 border border-steel-azure-200/40 rounded-huge p-8 sticky top-4 space-y-6">
                        {/* Movement detail header */}
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="text-[10px] uppercase tracking-widest text-calm-accent">
                              Detalle del movimiento
                            </p>
                            <p className="text-sm font-light text-calm-text mt-2">{movimientoActivo.concepto}</p>
                          </div>
                          <button
                            onClick={() => setMovimientoActivo(null)}
                            className="text-calm-accent hover:text-calm-text transition-colors"
                          >
                            <Icon name="close" className="text-[20px]" />
                          </button>
                        </div>

                        {/* Movement metadata */}
                        <div className="grid grid-cols-2 gap-3">
                          <div className="bg-calm-bg rounded-huge p-4">
                            <p className="text-[10px] uppercase tracking-tighter text-calm-accent">Fecha</p>
                            <p className="text-sm font-light text-calm-text tabular-nums mt-1">{formatDate(movimientoActivo.fecha)}</p>
                          </div>
                          <div className="bg-calm-bg rounded-huge p-4">
                            <p className="text-[10px] uppercase tracking-tighter text-calm-accent">Fecha valor</p>
                            <p className="text-sm font-light text-calm-text tabular-nums mt-1">{formatDate(movimientoActivo.fecha_valor)}</p>
                          </div>
                          <div className="bg-calm-bg rounded-huge p-4">
                            <p className="text-[10px] uppercase tracking-tighter text-calm-accent">Importe</p>
                            <p className={cn(
                              'text-sm font-light tabular-nums mt-1',
                              movimientoActivo.importe >= 0 ? 'text-emerald-600' : 'text-red-500'
                            )}>
                              {formatCurrency(movimientoActivo.importe)}
                            </p>
                          </div>
                          <div className="bg-calm-bg rounded-huge p-4">
                            <p className="text-[10px] uppercase tracking-tighter text-calm-accent">Estado</p>
                            <div className="mt-1.5">{estadoBadge(movimientoActivo.estado)}</div>
                          </div>
                        </div>

                        {movimientoActivo.referencia && (
                          <div className="bg-calm-bg rounded-huge p-4">
                            <p className="text-[10px] uppercase tracking-tighter text-calm-accent">Referencia</p>
                            <p className="text-sm font-mono font-light text-calm-muted mt-1">{movimientoActivo.referencia}</p>
                          </div>
                        )}

                        {/* Matched document/asiento info */}
                        {movimientoActivo.estado === 'conciliado' && (
                          <div className="bg-emerald-50 border border-emerald-200 rounded-huge p-4">
                            <div className="flex items-center gap-2 text-emerald-700 text-sm font-light">
                              <Icon name="check_circle" className="text-[18px]" />
                              <span>
                                {movimientoActivo.documento_id
                                  ? 'Asociado a documento'
                                  : movimientoActivo.asiento_id
                                  ? 'Asociado a asiento contable'
                                  : 'Conciliado'}
                              </span>
                            </div>
                            <p className="text-[11px] text-emerald-600 mt-1 font-light">
                              ID: {movimientoActivo.documento_id || movimientoActivo.asiento_id || '—'}
                            </p>
                          </div>
                        )}

                        {/* Suggestions */}
                        {movimientoActivo.estado !== 'conciliado' && (
                          <>
                            <div>
                              <p className="text-[10px] uppercase tracking-widest text-calm-accent mb-3">
                                Sugerencias de coincidencia
                              </p>
                              {loadingSugerencias ? (
                                <div className="flex items-center gap-2 text-calm-accent text-sm py-3 font-light">
                                  <Icon name="sync" className="text-[16px] animate-spin" />
                                  Buscando coincidencias...
                                </div>
                              ) : sugerencias.length > 0 ? (
                                <div className="space-y-2">
                                  {sugerencias.map(s => (
                                    <button
                                      key={s.id}
                                      className="w-full text-left border border-calm-border rounded-huge p-4 hover:bg-slate-50/50 hover:border-calm-text/20 transition-all group"
                                    >
                                      <div className="flex items-start justify-between w-full">
                                        <div>
                                          <div className="flex items-center gap-2">
                                            {s.tipo === 'factura' && <Icon name="description" className="text-[16px] text-calm-muted" />}
                                            {s.tipo === 'asiento' && <Icon name="account_balance" className="text-[16px] text-calm-muted" />}
                                            {s.tipo === 'regla' && <Icon name="bolt" className="text-[16px] text-amber-500" />}
                                            <span className="text-sm font-light text-calm-text">{s.descripcion}</span>
                                          </div>
                                          <p className="text-[11px] text-calm-accent mt-1">{formatDate(s.fecha)}</p>
                                        </div>
                                        <div className="text-right flex items-center gap-2">
                                          <Badge variant="outline" className={cn(
                                            'rounded-full text-[10px]',
                                            s.confianza >= 0.85
                                              ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                              : s.confianza >= 0.7
                                              ? 'bg-amber-50 text-amber-700 border-amber-200'
                                              : 'bg-slate-50 text-calm-muted border-calm-border'
                                          )}>
                                            {Math.round(s.confianza * 100)}%
                                          </Badge>
                                          <Icon name="arrow_forward" className="text-[16px] text-calm-accent group-hover:text-calm-text transition-colors" />
                                        </div>
                                      </div>
                                    </button>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-sm text-calm-accent py-2 font-light">Sin sugerencias automáticas</p>
                              )}
                            </div>

                            {/* Action buttons */}
                            <div className="space-y-2">
                              <button
                                onClick={handleBuscarDocumento}
                                className="w-full border border-calm-border rounded-huge px-5 py-2.5 text-sm font-light text-calm-muted hover:bg-white hover:shadow-sm transition-all flex items-center justify-center gap-2"
                              >
                                <Icon name="search" className="text-[18px]" />
                                Buscar Documento
                              </button>
                              <button
                                onClick={handleCrearAsientoDesdeMovimiento}
                                className="w-full border border-calm-border rounded-huge px-5 py-2.5 text-sm font-light text-calm-muted hover:bg-white hover:shadow-sm transition-all flex items-center justify-center gap-2"
                              >
                                <Icon name="add" className="text-[18px]" />
                                Crear Asiento
                              </button>
                              <button
                                onClick={handleCrearReglaDesdeMovimiento}
                                className="w-full rounded-huge px-5 py-2 text-[11px] font-light text-calm-accent hover:text-calm-muted transition-colors flex items-center justify-center gap-1.5"
                              >
                                <Icon name="bolt" className="text-[14px]" />
                                Crear regla para movimientos similares
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    ) : (
                      <div className="bg-gradient-to-br from-white to-fresh-sky-50/20 border border-fresh-sky-200/40 rounded-huge p-8 text-center py-16">
                        <Icon name="visibility" className="text-[32px] text-calm-accent mx-auto" />
                        <p className="text-sm font-light text-calm-muted mt-3">
                          Selecciona un movimiento
                        </p>
                        <p className="text-[11px] text-calm-accent mt-1">
                          Haz clic en una fila para ver detalles y sugerencias
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </TabsContent>

              {/* ═══════════════ RULES TAB ═══════════════ */}
              <TabsContent value="reglas" className="mt-6">
                <div className="space-y-6">
                  {/* Rules header */}
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-calm-muted font-light">
                      {reglas.length} regla(s) configurada(s) · {reglas.filter(r => r.activa).length} activa(s)
                    </p>
                    <div className="flex gap-3">
                      <button
                        onClick={handleGenerarReglasHistorico}
                        className="border border-calm-border rounded-huge px-5 py-2.5 text-sm font-light text-calm-muted hover:bg-white hover:shadow-sm transition-all flex items-center gap-2"
                      >
                        <Icon name="bolt" className="text-[18px]" />
                        Generar desde Histórico
                      </button>
                      <button
                        onClick={() => { setMostrarReglaForm(true); setEditingReglaId(null); setReglaForm({ nombre: '', condicion_campo: 'concepto', condicion_operador: 'contiene', condicion_valor: '', cuenta_contrapartida: '' }); }}
                        className="border border-calm-border rounded-huge px-5 py-2.5 text-sm font-light text-calm-text bg-white hover:shadow-sm transition-all flex items-center gap-2"
                      >
                        <Icon name="add" className="text-[18px]" />
                        Nueva Regla
                      </button>
                    </div>
                  </div>

                  {/* Rules table */}
                  <div className="bg-gradient-to-br from-white to-slate-50 border border-slate-200/60 rounded-huge overflow-hidden">
                    {reglas.length === 0 ? (
                      <div className="py-16 text-center">
                        <Icon name="settings" className="text-[32px] text-calm-accent mx-auto" />
                        <p className="text-sm text-calm-muted mt-3 font-light">No hay reglas configuradas</p>
                        <p className="text-[11px] text-calm-accent mt-1">Crea reglas para conciliar movimientos automáticamente</p>
                      </div>
                    ) : (
                      <Table>
                        <TableHeader>
                          <TableRow className="border-b border-calm-border">
                            <TableHead className="p-4 text-[11px] uppercase tracking-widest text-calm-accent font-normal">Nombre</TableHead>
                            <TableHead className="p-4 text-[11px] uppercase tracking-widest text-calm-accent font-normal">Condición</TableHead>
                            <TableHead className="p-4 text-[11px] uppercase tracking-widest text-calm-accent font-normal">Contrapartida</TableHead>
                            <TableHead className="p-4 text-center text-[11px] uppercase tracking-widest text-calm-accent font-normal">Aplicada</TableHead>
                            <TableHead className="p-4 text-center text-[11px] uppercase tracking-widest text-calm-accent font-normal">Activa</TableHead>
                            <TableHead className="p-4 text-center text-[11px] uppercase tracking-widest text-calm-accent font-normal w-24">Acciones</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {reglas.map(regla => (
                            <TableRow key={regla.id} className={cn(
                              'hover:bg-slate-50/50 transition-colors border-b border-calm-border/50 last:border-b-0',
                              !regla.activa && 'opacity-50'
                            )}>
                              <TableCell className="p-4 font-light text-calm-text text-sm">{regla.nombre}</TableCell>
                              <TableCell className="p-4 text-calm-muted text-sm font-light">
                                <span className="bg-calm-bg text-calm-muted px-2 py-0.5 rounded-md text-[11px] font-mono">
                                  {regla.condicion_campo}
                                </span>
                                <span className="text-calm-accent mx-1.5">{regla.condicion_operador}</span>
                                <span className="text-calm-text">&ldquo;{regla.condicion_valor}&rdquo;</span>
                              </TableCell>
                              <TableCell className="p-4 text-calm-muted font-mono text-[11px]">{regla.cuenta_contrapartida}</TableCell>
                              <TableCell className="p-4 text-center">
                                <Badge variant="outline" className="bg-slate-50 text-calm-muted border-calm-border rounded-full text-[10px]">
                                  {regla.veces_aplicada}×
                                </Badge>
                              </TableCell>
                              <TableCell className="p-4 text-center">
                                <Switch
                                  checked={regla.activa}
                                  onCheckedChange={() => handleToggleRegla(regla.id)}
                                />
                              </TableCell>
                              <TableCell className="p-4 text-center">
                                <div className="flex items-center justify-center gap-1">
                                  <button
                                    onClick={() => handleEditRegla(regla)}
                                    className="text-calm-accent hover:text-calm-text p-1.5 rounded-lg hover:bg-calm-bg transition-all"
                                    title="Editar"
                                  >
                                    <Icon name="edit" className="text-[16px]" />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteRegla(regla.id)}
                                    className="text-calm-accent hover:text-red-500 p-1.5 rounded-lg hover:bg-red-50 transition-all"
                                    title="Eliminar"
                                  >
                                    <Icon name="delete" className="text-[16px]" />
                                  </button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          )}

          {/* ═══════════════ EMPTY STATE ═══════════════ */}
          {!cuentaSeleccionada && (
            <div className="bg-gradient-to-br from-white to-fresh-sky-50/20 border border-fresh-sky-200/40 rounded-huge p-8 text-center py-16">
              <Icon name="account_balance" className="text-[40px] text-calm-accent mx-auto" />
              <h3 className="font-outfit text-xl font-light text-calm-text mt-4">
                Selecciona una empresa y cuenta bancaria
              </h3>
              <p className="text-calm-muted text-sm font-light max-w-md mx-auto mt-2">
                Elige una empresa y su cuenta bancaria para ver los movimientos, realizar conciliaciones
                y gestionar las reglas automáticas.
              </p>
            </div>
          )}
        </div>

        {/* ═══════════════ FLOATING RECONCILE BUTTON ═══════════════ */}
        {cuentaSeleccionada && activeTab === 'movimientos' && (
          <div className="fixed bottom-8 right-8 z-40">
            <button
              onClick={handleReconcileAuto}
              disabled={reconciling}
              className={cn(
                'rounded-huge px-6 py-3 text-sm font-light shadow-lg hover:shadow-xl transition-all flex items-center gap-2.5',
                reconciling
                  ? 'bg-calm-accent/30 cursor-wait text-calm-muted'
                  : 'bg-calm-text text-white hover:bg-slate-800'
              )}
            >
              <Icon name="sync" className={cn('text-[20px]', reconciling && 'animate-spin')} />
              {reconciling
                ? `Conciliando... ${reconcileProgress}%`
                : 'Conciliar Automáticamente'}
            </button>

            {/* Progress bar during reconciliation */}
            {reconciling && (
              <div className="absolute -bottom-2 left-0 right-0">
                <Progress value={reconcileProgress} className="h-1 rounded-full" />
              </div>
            )}
          </div>
        )}

        {/* ═══════════════ IMPORT MODAL ═══════════════ */}
        <Dialog open={mostrarImportModal} onOpenChange={(open) => !importing && setMostrarImportModal(open)}>
          <DialogContent className="max-w-lg rounded-huge border-calm-border">
            <DialogHeader>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-huge bg-calm-bg flex items-center justify-center">
                  <Icon name="upload" className="text-[20px] text-calm-muted" />
                </div>
                <div>
                  <DialogTitle className="font-outfit font-light text-calm-text">Importar Movimientos</DialogTitle>
                  <p className="text-[11px] text-calm-accent mt-0.5">Sube un archivo con los movimientos bancarios</p>
                </div>
              </div>
            </DialogHeader>

            <div className="space-y-5">
              {/* Format selector */}
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-calm-accent mb-2 block">Formato del archivo</Label>
                <div className="grid grid-cols-3 gap-2">
                  {(['norma43', 'csv', 'excel'] as const).map(fmt => (
                    <button
                      key={fmt}
                      onClick={() => setImportFormato(fmt)}
                      className={cn(
                        'rounded-huge border-2 px-4 py-2.5 text-sm font-light transition-all',
                        importFormato === fmt
                          ? 'border-calm-text bg-calm-bg text-calm-text'
                          : 'border-calm-border text-calm-muted hover:border-calm-accent'
                      )}
                    >
                      {fmt === 'norma43' ? 'Norma 43' : fmt === 'csv' ? 'CSV' : 'Excel'}
                    </button>
                  ))}
                </div>
              </div>

              {/* File upload area */}
              <div
                onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={e => {
                  e.preventDefault();
                  setIsDragging(false);
                  const file = e.dataTransfer.files[0];
                  if (file) handleFileSelect(file);
                }}
                className={cn(
                  'border-2 border-dashed rounded-huge p-10 text-center transition-all cursor-pointer',
                  isDragging
                    ? 'border-calm-text bg-calm-bg/50'
                    : importFile
                    ? 'border-calm-accent bg-calm-bg/50'
                    : 'border-calm-border hover:border-calm-accent'
                )}
                onClick={() => {
                  const input = document.createElement('input');
                  input.type = 'file';
                  input.accept = importFormato === 'norma43' ? '.n43,.txt' : importFormato === 'csv' ? '.csv' : '.xlsx,.xls';
                  input.onchange = e => {
                    const file = (e.target as HTMLInputElement).files?.[0];
                    if (file) handleFileSelect(file);
                  };
                  input.click();
                }}
              >
                {importFile ? (
                  <div>
                    <Icon name="check_circle" className="text-[32px] text-emerald-500 mx-auto" />
                    <p className="text-sm font-light text-calm-text mt-2">{importFile.name}</p>
                    <p className="text-[11px] text-calm-accent mt-1">
                      {(importFile.size / 1024).toFixed(1)} KB · Clic para cambiar
                    </p>
                  </div>
                ) : (
                  <div>
                    <Icon name="upload" className="text-[32px] text-calm-accent mx-auto" />
                    <p className="text-sm text-calm-muted mt-2 font-light">Arrastra un archivo aquí o haz clic para seleccionar</p>
                    <p className="text-[11px] text-calm-accent mt-1">
                      {importFormato === 'norma43' ? '.n43, .txt' : importFormato === 'csv' ? '.csv' : '.xlsx, .xls'}
                    </p>
                  </div>
                )}
              </div>

              {/* Preview */}
              {importPreview.length > 0 && (
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-calm-accent mb-2">
                    Vista previa (primeros {importPreview.length} movimientos)
                  </p>
                  <div className="bg-gradient-to-br from-white to-fresh-sky-50/20 border border-fresh-sky-200/40 rounded-huge overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-b border-calm-border">
                          <TableHead className="p-3 text-[11px] uppercase tracking-widest text-calm-accent font-normal">Fecha</TableHead>
                          <TableHead className="p-3 text-[11px] uppercase tracking-widest text-calm-accent font-normal">Concepto</TableHead>
                          <TableHead className="p-3 text-right text-[11px] uppercase tracking-widest text-calm-accent font-normal">Importe</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {importPreview.map((row, i) => (
                          <TableRow key={i} className="hover:bg-slate-50/50 border-b border-calm-border/50 last:border-b-0">
                            <TableCell className="p-3 text-calm-muted tabular-nums text-[11px] font-light">{row.fecha}</TableCell>
                            <TableCell className="p-3 text-calm-text truncate max-w-[200px] text-[11px] font-light">{row.concepto}</TableCell>
                            <TableCell className={cn(
                              'p-3 text-right font-light tabular-nums text-[11px]',
                              row.importe >= 0 ? 'text-emerald-600' : 'text-red-500'
                            )}>
                              {row.importe >= 0 ? '+' : ''}{row.importe.toFixed(2)}€
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              {/* Import progress */}
              {importing && (
                <div>
                  <div className="flex items-center justify-between text-[11px] text-calm-accent mb-1.5">
                    <span>Importando...</span>
                    <span>{importProgress}%</span>
                  </div>
                  <Progress value={importProgress} className="h-1.5 rounded-full" />
                </div>
              )}
            </div>

            <DialogFooter>
              <button
                onClick={() => { setMostrarImportModal(false); setImportFile(null); setImportPreview([]); }}
                disabled={importing}
                className="border border-calm-border rounded-huge px-5 py-2.5 text-sm font-light text-calm-muted hover:bg-white hover:shadow-sm transition-all disabled:opacity-40"
              >
                Cancelar
              </button>
              <button
                onClick={handleImportFile}
                disabled={!importFile || importing}
                className="border border-calm-text rounded-huge px-5 py-2.5 text-sm font-light bg-calm-text text-white hover:bg-slate-800 transition-all disabled:opacity-40 flex items-center gap-2"
              >
                {importing ? (
                  <>
                    <Icon name="sync" className="text-[16px] animate-spin" />
                    Importando...
                  </>
                ) : (
                  <>
                    <Icon name="download" className="text-[16px]" />
                    Importar Movimientos
                  </>
                )}
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ═══════════════ RULE FORM MODAL ═══════════════ */}
        <Dialog open={mostrarReglaForm} onOpenChange={setMostrarReglaForm}>
          <DialogContent className="max-w-md rounded-huge border-calm-border">
            <DialogHeader>
              <DialogTitle className="font-outfit font-light text-calm-text">
                {editingReglaId ? 'Editar Regla' : 'Nueva Regla de Conciliación'}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-calm-accent mb-1.5 block">Nombre de la regla *</Label>
                <Input
                  value={reglaForm.nombre}
                  onChange={e => setReglaForm({ ...reglaForm, nombre: e.target.value })}
                  placeholder="Ej: Recibos de luz"
                  className="border-calm-border rounded-huge text-sm font-light"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-calm-accent mb-1.5 block">Campo</Label>
                  <Select
                    value={reglaForm.condicion_campo}
                    onValueChange={v => setReglaForm({ ...reglaForm, condicion_campo: v })}
                  >
                    <SelectTrigger className="border-calm-border rounded-huge text-sm font-light">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="concepto">Concepto</SelectItem>
                      <SelectItem value="importe">Importe</SelectItem>
                      <SelectItem value="referencia">Referencia</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-calm-accent mb-1.5 block">Operador</Label>
                  <Select
                    value={reglaForm.condicion_operador}
                    onValueChange={v => setReglaForm({ ...reglaForm, condicion_operador: v })}
                  >
                    <SelectTrigger className="border-calm-border rounded-huge text-sm font-light">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="contiene">Contiene</SelectItem>
                      <SelectItem value="igual">Igual a</SelectItem>
                      <SelectItem value="empieza">Empieza por</SelectItem>
                      <SelectItem value="mayor">Mayor que</SelectItem>
                      <SelectItem value="menor">Menor que</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label className="text-[10px] uppercase tracking-widest text-calm-accent mb-1.5 block">Valor de búsqueda *</Label>
                <Input
                  value={reglaForm.condicion_valor}
                  onChange={e => setReglaForm({ ...reglaForm, condicion_valor: e.target.value })}
                  placeholder="Ej: ENDESA, TELEFÓNICA..."
                  className="border-calm-border rounded-huge text-sm font-light"
                />
              </div>

              <div>
                <Label className="text-[10px] uppercase tracking-widest text-calm-accent mb-1.5 block">Cuenta contrapartida *</Label>
                <Input
                  value={reglaForm.cuenta_contrapartida}
                  onChange={e => setReglaForm({ ...reglaForm, cuenta_contrapartida: e.target.value })}
                  className="font-mono border-calm-border rounded-huge text-sm font-light"
                  placeholder="Ej: 6290000"
                />
              </div>
            </div>

            <DialogFooter>
              <button
                onClick={() => setMostrarReglaForm(false)}
                className="border border-calm-border rounded-huge px-5 py-2.5 text-sm font-light text-calm-muted hover:bg-white hover:shadow-sm transition-all"
              >
                Cancelar
              </button>
              <button
                onClick={handleSaveRegla}
                className="border border-calm-text rounded-huge px-5 py-2.5 text-sm font-light bg-calm-text text-white hover:bg-slate-800 transition-all"
              >
                {editingReglaId ? 'Guardar Cambios' : 'Crear Regla'}
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </Layout>
    </>
  );
}
